import { useEffect, useState } from 'react';

import { CallStatus, PredefinedMilisecondsTypeEnum } from '../../lib/enums';

import Button from '../../shared_components/Button/Button';
import CompanyLogoCheckmark from '../CompanyLogoCheckmark/CompanyLogoCheckmark';
import { MobileDrawer } from '../index';

import styles from './FinalPopupOrderMobile.module.scss';

type Props = {
  processFailedMessage: string;
  processSuccessMessageTitle: string;
  toggleModal: (arg?: any) => void;
  logo?: string;
  logoURL?: string;
  callStatus?: CallStatus;
  processSuccessMessageBody?: string;
  processPendingMessage?: { title: string, body: string };
  defaultMessageTitle?: string;
  defaultMessageBody?: string;
  processPartiallyRejectedMessage?: { title: string, body: string };
  processRejectedMessage?: { title: string, body: string };
  symbol?: string;
};

const ProcessFinishedModal = (
  {
    callStatus,
    toggleModal,
    logo,
    logoURL,
    processFailedMessage,
    processSuccessMessageBody,
    processSuccessMessageTitle,
    processPendingMessage,
    processPartiallyRejectedMessage,
    processRejectedMessage,
    defaultMessageTitle,
    defaultMessageBody,
    symbol,
  }: Props,
): JSX.Element => {
  const [modalOpened, setModalOpened] = useState<boolean>(false);

  const errorMessageRender = () => {
    if (processPartiallyRejectedMessage) {
      return (
        <>
          <div className={styles.title}>{processPartiallyRejectedMessage.title}</div>
          <div className={styles.text}>{processPartiallyRejectedMessage.body}</div>
        </>
      );
    }

    if (processRejectedMessage) {
      return (
        <div className={styles.titleAndDesc}>
          <div className={styles.title}>{processRejectedMessage.title}</div>
          <div className={styles.text}>{processRejectedMessage.body}</div>
        </div>
      );
    }

    return (<div className={styles.title}>{processFailedMessage}</div>);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setModalOpened(true);
    }, PredefinedMilisecondsTypeEnum.oneSecond);

    return (() => {
      clearTimeout(timer);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getModalBody = (): JSX.Element => {
    if (callStatus === CallStatus.READY) {
      return (
        <div className={styles.messageAndLogo}>
          {(logo || logoURL) && <CompanyLogoCheckmark url={logoURL} logo={logo} symbol={symbol} />}
          <div className={styles.titleAndDesc}>
            <div className={styles.title}>{processSuccessMessageTitle}</div>
            {processSuccessMessageBody && <div className={styles.text}>{processSuccessMessageBody}</div>}
          </div>
        </div>
      );
    }

    if (processPendingMessage) {
      return (
        <div className={styles.messageAndLogo}>
          {(logo || logoURL) && <CompanyLogoCheckmark url={logoURL} logo={logo} />}
          <div className={styles.titleAndDesc}>
            <div className={styles.title}>{processPendingMessage.title}</div>
            <div className={styles.text}>{processPendingMessage.body}</div>
          </div>
        </div>
      );
    }
    if (defaultMessageTitle) {
      return (
        <div className={styles.messageAndLogo}>
          {(logo || logoURL) && (
            <CompanyLogoCheckmark
              url={logoURL}
              logo={logo}
            />
          )}
          <div className={styles.titleAndDesc}>
            <div className={styles.title}>{defaultMessageTitle}</div>
            {defaultMessageBody && <div className={styles.text}>{defaultMessageBody}</div>}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.messageAndLogo}>
        {logo && <CompanyLogoCheckmark isCancel logo={logo} />}
        {errorMessageRender()}
      </div>
    );
  };

  if (!modalOpened) {
    return <></>;
  }

  return (
    <MobileDrawer
      isOpen={modalOpened}
      title=""
      showTitle={false}
      showCloseButton={false}
      onClose={() => toggleModal(false)}
    >
      <div className={styles.logoAndNameWrapper}>
        <div className={styles.textWrapper}>
          {getModalBody()}
        </div>
      </div>
      <div className={styles.buttonsWrapper}>
        <Button
          fullWidth
          biggerFontSize
          onClick={() => toggleModal(false)}
        >
          Okay
        </Button>
      </div>
    </MobileDrawer>
  );
};

export default ProcessFinishedModal;
